import React, { useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { PlxsButton } from "@plexlogic/plexus-ui";
import DrawWizard from "./components/DrawWizard";
import DrawDetailComponent from "./components/drawDetails/DrawDetailComponent";
import AuditDashboard from "./components/auditing/AuditDashboard";
import UserDashboard from "./components/userDashboad/UserDashboard";
import Loading from "./components/Loading";
import "./App.css";
import AppHeader from "./components/AppHeader";
import {
  NotificationProvider,
  useNotification,
} from "./contexts/NotificationContext";
import RandomnessMonitor from "./components/RandomnessMonitor";
import RandomnessTest from "./components/RandomnessTest";
import ErrorFallback from "./components/ErrorFallback";
import SuperUserRoute from "./components/SuperUserRoute";
import { setTokenProvider, setNotificationFunction, setRefreshTokenFunction, setLoginRedirectFunction, setLogoutRedirectFunction } from "./services/api";
import { hasWizardSession } from "./services/wizardStorage";
import { ErrorBoundary } from "react-error-boundary";
import { ThemeContextProvider, useTheme } from "./contexts/ThemeContext";
import ThemeProvider from "./contexts/ThemeProvider";
import { toSentenceCase } from "./utils/stringUtils";
import { usePendo, useIntercom } from "./hooks";

function BackButton() {
  const location = useLocation();
  const showBackButton =
    location.pathname === "/monitoring" ||
    location.pathname === "/random-health" ||
    location.pathname === "/audit";

  if (!showBackButton) return null;

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px 2rem 0 2rem",
        paddingTop: "calc(20px + 80px)", // Account for header height
      }}
    >
      <div style={{ marginBottom: "20px" }}>
        <PlxsButton
          functionalIcon="arrowLeft"
          label={toSentenceCase("back to dashboard")}
          onClick={() => (window.location.href = "/dashboard")}
          variant="secondary"
          tone="brand"
          size="md"
          dataTestId="draw-nav-back-to-dashboard-button"
        />
      </div>
    </div>
  );
}

function MainContent() {
  const location = useLocation();
  const isDashboardPage = location.pathname === "/dashboard" || location.pathname === "/";
  const isDrawDetailPage = location.pathname.startsWith("/draws/") && location.pathname !== "/draws/wizard";

  return (
    <div
      style={{
        maxWidth: isDashboardPage ? "100%" : "1200px",
        margin: "0 auto",
        padding: isDashboardPage ? "0" : (isDrawDetailPage ? "0" : "var(--plexus-space-lg, 2rem)"),
        paddingTop: isDashboardPage
          ? "0"
          : (isDrawDetailPage ? "0" : "20px"), // Account for header height
        backgroundColor: "var(--plexus-color-body-bg)",
        minHeight: isDrawDetailPage ? "100vh" : "calc(100vh - 60px)", // Full viewport for draw detail, otherwise full height minus header
        height: isDrawDetailPage ? "100vh" : "auto", // Fixed height for draw detail page
        overflow: isDrawDetailPage ? "hidden" : "visible", // Prevent outer scroll on draw detail page
      }}
    >
      <Switch>
        <SuperUserRoute exact path="/monitoring" component={RandomnessMonitor} />
        <SuperUserRoute exact path="/random-health" component={RandomnessTest} />
        <Route exact path="/audit" component={AuditDashboard} />
        <Route exact path="/dashboard" component={UserDashboard} />
        <Route exact path="/draws/wizard" component={DrawWizard} />
        <Route path="/draws/:drawId" component={DrawDetailComponent} />
        <Route exact path="/" component={UserDashboard} />
      </Switch>
    </div>
  );
}

function AppContent() {
  const {
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout,
    refreshAccessToken,
  } = useAuth();
  const { currentTheme } = useTheme();
  const { showNotification } = useNotification();
  const isWizardRoute = window.location.pathname === "/draws/wizard";
  const isAnonymousDrawsRoute =
    window.location.pathname.startsWith("/draws/");

  // After auth redirect, if wizard session data exists, redirect to the wizard route
  // so DrawWizard mounts and can restore the user's progress
  useEffect(() => {
    if (isAuthenticated && hasWizardSession() && !isWizardRoute) {
      window.location.href = "/draws/wizard";
    }
  }, [isAuthenticated, isWizardRoute]);

  // Initialize Pendo analytics after authentication
  usePendo();

  useIntercom();

  // Set up the token provider for API calls
  useEffect(() => {
    if (isAuthenticated) {
      const tokenProvider = async () => {
        try {
          // Read directly from localStorage to get the most current token
          // This is important after token refresh where React state may not have updated yet
          const token = localStorage.getItem('access_token');
          
          // Handle cases where token might be the string "null" or "undefined"
          if (!token || token === 'null' || token === 'undefined') {
            return null;
          }
          
          return token;
        } catch (error) {
          // Token expired, timeout, or auth issues - force re-authentication
          loginWithRedirect();
          return null;
        }
      };
      setTokenProvider(tokenProvider);
    } else {
      setTokenProvider(async () => null);
    }
  }, [isAuthenticated, loginWithRedirect]);

  // Set up notification function for API service
  useEffect(() => {
    setNotificationFunction(showNotification);
  }, [showNotification]);

  // Set up refresh token function for API service
  useEffect(() => {
    setRefreshTokenFunction(refreshAccessToken);
  }, [refreshAccessToken]);

  // Set up login redirect function for API service
  useEffect(() => {
    const wrappedLoginRedirect = () => {
      // Set flag to suppress navigation warnings during login redirect
      sessionStorage.setItem('isRedirectingToLogin', 'true');
      loginWithRedirect();
    };
    setLoginRedirectFunction(wrappedLoginRedirect);
  }, [loginWithRedirect]);

  // Set up logout redirect function for API service (when token refresh fails)
  useEffect(() => {
    setLogoutRedirectFunction(logout);
  }, [logout]);

  if (isLoading && !isWizardRoute) {
    return <Loading />;
  }

  if (error) {
    return (
      <ErrorFallback
        error={error}
        resetErrorBoundary={() => window.location.reload()}
      />
    );
  }

  return (
    <ThemeProvider currentTheme={currentTheme}>
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "var(--plexus-color-body-bg)",
        }}
      >
        <Router>
          <AppHeader />
          <BackButton />

          {(isAuthenticated || isAnonymousDrawsRoute) && <MainContent />}
        </Router>
      </div>
    </ThemeProvider>
  );
}

// ErrorFallback component now imported from ./components/ErrorFallback

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeContextProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </ThemeContextProvider>
    </ErrorBoundary>
  );
}

export default App;
